import React, { FC, useState, useMemo, useCallback } from 'react'
import { UseFormMethods } from 'react-hook-form'
import pick from 'lodash/pick'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import { Truck, ITruckProps } from 'apps/fleet-planner/pages/loads/Truck'
import { ILoad } from 'apps/fleet-planner/types'

interface IControlledTruckProps {
  name?: string
  defaultValue?: Partial<Pick<ILoad, 'truckId' | 'truckNumber' | 'truck'>>
  TruckProps?: Partial<ITruckProps>
  control: UseFormMethods['control']
  setValue: UseFormMethods['setValue']

  // Sets value using truckNumber instead of id
  useNumber?: boolean
}

/**
 * Helper component for use with React Hook Form
 * @see https://react-hook-form.com/api#Controller
 * @example
 * ```
 * <ControlledTruck
 *   control={control}
 *   TruckProps={{
 *     selectedMotorCarrierId: 90210
 *   }} />
 * ```
 */
export const ControlledTruck: FC<IControlledTruckProps> = ({
  name = 'truckId',
  defaultValue = {},
  TruckProps = {},
  control,
  setValue,
  useNumber = false,
}) => {
  const [load, setLoad] = useState<Partial<ILoad>>({
    ...defaultValue,
  })

  const handleChange = (change: Partial<ILoad> | undefined) => {
    const { truck } = change || {}
    setValue(name, useNumber ? truck?.truckNumber : truck?.id)
    setLoad(prev => ({
      ...prev,
      ...change,
    }))
  }

  const { error = '', large = true, refetchOptions = false, selectedMotorCarrierId } =
    TruckProps || {}

  const { truckId: defaultTruckId } = defaultValue || {}

  /**
   * Formats the value using `truckId` as a backup value
   */
  const getValue = useCallback((): Pick<ILoad, 'truck'> => {
    return pick(load, 'truck')
  }, [load])

  const value = useMemo(() => getValue(), [getValue])

  return (
    <>
      <Truck
        large={large}
        value={value}
        onChange={handleChange}
        refetchOptions={refetchOptions}
        error={error}
        selectedMotorCarrierId={selectedMotorCarrierId}
      />
      {/**
       * Register inputs for use with RHF
       */}
      <ControlledHiddenInput name={name} defaultValue={defaultTruckId} control={control} />
    </>
  )
}
